import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Components 

import Box from '@mui/material/Box';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import PersonIcon from '@mui/icons-material/Person';
import UpdateIcon from '@mui/icons-material/Update';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import getStatusColor from '../../../../util/style/getStatusColor';
import InfoRow from '../../../general/inputs/InfoRow';
import SignalWifiStatusbar4BarIcon from '@mui/icons-material/SignalWifiStatusbar4Bar';
import { getDoctorName, getGroup, getGroupAccessListWithGroupId, getGroupSize, updateGroupMainDoctor, updateGroupSeats, updateGroupStatus } from '../../../../routes/hacker/users';
import moment from 'moment';
import TollIcon from '@mui/icons-material/Toll';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { setNotificationMessage } from '../../../../redux/features/general/notification';
import { setIsRefreshGroups, setSelectedGroup } from '../../../../redux/features/hacker/users/groupsAccess';
import SettingsIcon from '@mui/icons-material/Settings';
import { seatsList, statusList } from './GroupSettingsOptions';
import getCapitalizedString from '../../../../util/style/getCapitalizedString';

interface Props { 

}; 

const GroupSettings: React.FC<Props> = ({ }) => {

    const dispatch = useAppDispatch(); 
    const { t } = useTranslation(); 
    const navigate = useNavigate(); 

    const colors = useAppSelector(state => state.theme.colors); 

    const selectedGroup = useAppSelector((state) => state.groupsAccess.selectedGroup); 

    const [doctorName, setDoctorName] = useState(''); 
    const [groupSize, setGroupSize] = useState(''); 
    const [groupAccessList, setGroupAccessList] = useState([]); 
    

    const handleGroup = async () => { 

      const res = await getGroup(selectedGroup._id) as any; 

      if (res.data.status === 200) { 

        dispatch(setSelectedGroup(res.data.group)); 

      }; 

    }; 

    // -- 

    const handleDoctorInfo = async () => { 

      if (selectedGroup.mainDoctorId) { 

        const res = await getDoctorName(selectedGroup.mainDoctorId) as any; 
  
        if (res.data.status === 200) { 
    
          setDoctorName(res.data.fullName); 
    
        }; 

      }; 
  
    }; 

    // --

    const handleGroupSize = async () => { 
  
      const res = await getGroupSize(selectedGroup?._id) as any;
  
      if (res.data.status === 200) { 
  
        setGroupSize(res.data.size); 
  
      }; 
  
    }; 

    // --

    const getGroupAccessList = async () => { 

      const res = await getGroupAccessListWithGroupId(selectedGroup?._id) as any; 

      if (res.data.status === 200) { 

        setGroupAccessList(res.data.groupAccessList);  

      };

    }; 

    // -- 

    const handleGroupSeats = async (item: any) => { 

      if (item) { 

        const res = await updateGroupSeats(selectedGroup?._id, item.seats) as any; 

        if (res.data.status === 200) { 
  
          handleGroup();
  
          dispatch(setNotificationMessage('Group Seats Number Successfully Updated.')); 
  
        }; 

      }; 

    }; 

    // -- 

    const handleGroupStatus = async (item: any) => { 

      if (item) { 

        const res = await updateGroupStatus(selectedGroup?._id, item.status) as any; 

        if (res.data.status === 200) { 
  
          handleGroup();
  
          dispatch(setNotificationMessage('Group Status Successfully Updated.')); 
  
        }; 

      }; 

    }; 

    // -- 

    const handleGroupMainDoctorId = async (groupAccess: any) => { 

      if (groupAccess) { 

        if (groupAccess._id) { 

          const res = await updateGroupMainDoctor(groupAccess._id) as any; 

          if (res.data.status === 200) { 
    
            handleGroup();
    
            dispatch(setNotificationMessage('Group Main Doctor Id Successfully Updated.')); 
    
          }; 

        };

      }; 

    }; 
  
    // -- 
  
    useEffect(() => { 
  
      if (selectedGroup) { 
  
        handleDoctorInfo(); 

        handleGroupSize(); 

        getGroupAccessList(); 

        dispatch(setIsRefreshGroups(true)); 
  
      }; 
  
    },[selectedGroup]); 
    

    return (

      <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', 
          width: '50%',
          height: '95%', 
      }}>

        <Box
            sx={{ 
                display: 'flex',
                flex: 'row',
                alignItems: 'center',
                width: '100%', 
                color: colors?.text, 
                marginBottom: 2,
                paddingLeft: 3, 
                fontWeight: 'bold'
            }}
        >Information
       </Box>  

        <InfoRow 
          title='Clinic Name'
          icon={<ApartmentIcon sx={{ color: colors?.main }} />}
          description={selectedGroup?.name}
        />

        <InfoRow 
          title='Status'
          icon={<SignalWifiStatusbar4BarIcon sx={{ color: colors?.main }} />}
          description={ 
          <strong 
            style={{ 
              padding: 5,
              borderRadius: 7,
              backgroundColor: getStatusColor(selectedGroup?.status), 
              color: 'white', 
            }}>
          {getCapitalizedString(selectedGroup?.status)}
          </strong>}
          listHandler={handleGroupStatus}
          listIcon={<SettingsIcon sx={{ color: colors?.main }} />}
          dataList={statusList}
          rowIcon={<TollIcon sx={{ color: 'blue' }} />}
          rowTitle={'title'}
          rowDescription={'status'}
        />

        <InfoRow 
          title='Seats'
          icon={<TollIcon sx={{ color: colors?.main }} />}
          description={selectedGroup?.seats}
          listHandler={handleGroupSeats}
          listIcon={<SettingsIcon sx={{ color: colors?.main }} />}
          dataList={seatsList}
          rowIcon={<TollIcon sx={{ color: 'purple' }} />}
          rowTitle={'title'}
          rowDescription={'seats'}
        />

        <InfoRow 
          title='Admin'
          icon={<PersonIcon sx={{ color: colors?.main }} />}
          description={doctorName}
          listHandler={handleGroupMainDoctorId}
          listIcon={<RestartAltIcon sx={{ color: colors?.main }} />}
          dataList={groupAccessList}
          rowTitle={'_id'}
          rowIcon={<PersonIcon sx={{ color: 'purple' }} />}
          rowDescription={'status'}
        />

        <InfoRow 
          title='Group Size'
          icon={<SupervisedUserCircleIcon sx={{ color: colors?.main }} />}
          description={`${groupSize} Member${Number(groupSize) === 1 ? '' : 's'}`}
        />

        <InfoRow 
          title='Last Updated'
          icon={<UpdateIcon sx={{ color: colors?.main }} />}
          description={moment(selectedGroup?.updatedAt).utc(true).format('LLLL')}
          descriptionStyle={{ fontSize: 12,  color: 'rgb(150, 150, 150)'}}
        />

        <InfoRow 
          title='Created On'
          icon={<CalendarMonthIcon sx={{ color: colors?.main }} />}
          description={moment(selectedGroup?.createdAt).utc(true).format('LLLL')}
          descriptionStyle={{ fontSize: 12,  color: 'rgb(150, 150, 150)'}}
        />

      </Box>

  );

};

export default GroupSettings;